<svelte:options accessors />

<script context="module">
  export const EVENTS = {
    confirm: 'confirm',
    cancel: 'cancel',
    change: 'change',
    scrollInto: 'scrollInto',
    clickOption: 'clickOption',
    updateModelValue: 'updateModelValue',
  };

  const DEFAULT_ITEM_HEIGHT = 44;
</script>

<script lang="ts">
  import { createEventDispatcher, SvelteComponent } from 'svelte';

  import PickerColumn from './picker-column.svelte';
  import PickerToolbar from './picker-toolbar.svelte';

  export let readonly = false;
  export let allowHtml = true;
  export let itemHeight = DEFAULT_ITEM_HEIGHT;
  export let showToolbar = true;
  export let swipeDuration = 1000;
  export let visibleItemCount = 6;

  // toolbar props
  export let title = '地区';
  export let cancelButtonText = '取消';
  export let confirmButtonText = '确定';

  export let defaultIndex = 0;
  export let columns: Array<any> = [];
  export let valueKey = 'text';

  const dispatch = createEventDispatcher();

  const children: Array<SvelteComponent> = [];
  let formattedColumns: Array<{
    values: any;
    defaultIndex?: number;
    className?: string;
  }> = [];

  $: itemPxHeight = itemHeight || DEFAULT_ITEM_HEIGHT;

  let dataType = 'text';
  $: {
    const firstColumn = columns[0] || {};
    if (firstColumn.children) {
      dataType = 'cascade';
    } else if (firstColumn.values) {
      dataType = 'object';
    }
  }

  $: {
    if (dataType === 'text') {
      formattedColumns = [{ values: columns }];
    } else if (dataType === 'cascade') {
      formatCascade();
    } else {
      formattedColumns = columns;
    }
  }

  const formatCascade = () => {
    const formatted = [];

    let cursor = { children: columns };

    while (cursor && cursor.children) {
      const { children } = cursor;
      // @ts-ignore
      let defaultIdx = cursor.defaultIndex ?? +defaultIndex;

      while (children[defaultIdx] && children[defaultIdx].disabled) {
        if (defaultIdx < children.length - 1) {
          defaultIdx += 1;
        } else {
          defaultIdx = 0;
          break;
        }
      }

      formatted.push({
        values: cursor.children,
        // @ts-ignore
        className: cursor.className || '',
        defaultIndex: defaultIdx,
      });

      cursor = children[defaultIdx];
    }

    formattedColumns = formatted;
  };

  const onCascadeChange = (index: number) => {
    let columnIndex = index;
    let cursor = { children: columns };
    const indexes = getIndexes();

    for (let i = 0; i <= columnIndex; i++) {
      cursor = cursor.children[indexes[i]];
    }

    while (cursor && cursor.children) {
      columnIndex += 1;
      setColumnValues(columnIndex, cursor.children);
      // @ts-ignore
      cursor = cursor.children[cursor.defaultIndex || 0];
    }
  };

  const onChange = (columnIndex: number) => {
    if (dataType === 'cascade') {
      onCascadeChange(columnIndex);
    }

    if (dataType === 'text') {
      dispatch(EVENTS.change, {
        value: getColumnValue(0),
        index: getColumnIndex(0),
      });
    } else {
      let values = getValues();

      if (dataType === 'cascade') {
        values = values.map(item => item[valueKey]);
      }

      dispatch(EVENTS.change, {
        values,
        columnIndex,
      });
    }
  };

  const getColumn = (index: number) => children[index];

  export const getColumnValue = (index: number) => {
    const column = getColumn(index);
    return column && column.getValue();
  };

  export const setColumnValue = (index: number, value: any) => {
    const column = getColumn(index);

    if (column) {
      column.setValue(value);

      if (dataType === 'cascade') {
        onCascadeChange(index);
      }
    }
  };

  export const getColumnIndex = (columnIndex: number) => {
    const column = getColumn(columnIndex);
    if (column) {
      return column.getCurrentIndex();
    }
    return -1;
  };

  export const setColumnIndex = (columnIndex: number, optionIndex: number) => {
    const column = getColumn(columnIndex);

    if (column) {
      column.setIndex(optionIndex);

      if (dataType === 'cascade') {
        onCascadeChange(columnIndex);
      }
    }
  };

  export const getColumnValues = (index: number) => (children[index] || {}).options;

  export const setColumnValues = (index: number, options: any) => {
    const column = children[index];

    if (column) {
      column.setOptions(options);
    }
  };

  export const getValues = () => children.map(child => child.getValue());

  export const setValues = (values: Array<any>) => {
    values.forEach((value, index) => {
      setColumnValue(index, value);
    });
  };

  export const getIndexes = () => children.map(child => child.getCurrentIndex());

  export const setIndexes = (indexes: Array<number>) => {
    indexes.forEach((optionIndex, columnIndex) => {
      setColumnIndex(columnIndex, optionIndex);
    });
  };

  const emit = (event: string) => {
    if (dataType === 'text') {
      dispatch(event, {
        value: getColumnValue(0),
        index: getColumnIndex(0),
      });
    } else {
      let values = getValues();
      if (dataType === 'cascade') {
        values = values.map(item => item[valueKey]);
      }

      dispatch(event, {
        values,
        indexes: getIndexes(),
      });
    }
  };

  export const confirm = () => {
    children.forEach((child) => {
      child.stopMomentum();
    });
    emit(EVENTS.confirm);
  };

  export const cancel = () => {
    emit(EVENTS.cancel);
  };

  let maskStyle = {
    backgroundSize: '100% 0',
  };
  let columnsStyle = { height: '0px' };
  $: {
    const wrapHeight = itemPxHeight * visibleItemCount;
    maskStyle = {
      backgroundSize: `100% ${(wrapHeight - itemPxHeight) / 2}px`,
    };
    columnsStyle = { height: `${wrapHeight}px` };
  }
  $: frameStyle = { height: `${itemPxHeight}px` };
</script>

<div class="iwan-picker">
  {#if showToolbar}
    <PickerToolbar {cancelButtonText} {confirmButtonText} {title} on:confirm={confirm} on:cancel={cancel} />
  {/if}
  <div class="iwan-picker-columns" style:height={columnsStyle.height} on:touchmove|stopPropagation>
    {#each formattedColumns as item, index}
      <PickerColumn
        bind:this={children[index]}
        {readonly}
        {valueKey}
        {allowHtml}
        className={item.className}
        defaultIndex={item.defaultIndex ?? +defaultIndex}
        {swipeDuration}
        {visibleItemCount}
        initialOptions={item.values}
        itemHeight={itemPxHeight}
        on:change={() => {
          onChange(index);
        }}
      />
    {/each}
    <div class="iwan-picker-mask" style:background-size={maskStyle.backgroundSize} />
    <div class="iwan-picker-frame" style:height={frameStyle.height} />
  </div>
</div>

<style lang="scss">
  .iwan-picker {
    position: relative;
    background-color: #ffffff;
    user-select: none;
    &-columns {
      position: relative;
      display: -webkit-box;
      display: -webkit-flex;
      display: flex;
      cursor: grab;
    }
    &-frame {
      position: absolute;
      top: 50%;
      right: 16px;
      left: 16px;
      z-index: 2;
      transform: translateY(-50%);
      pointer-events: none;
    }
    &-mask {
      position: absolute;
      top: 0;
      left: 0;
      z-index: 1;
      width: 100%;
      height: 100%;
      background-image: -webkit-linear-gradient(top, hsla(0, 0%, 100%, 0.9), hsla(0, 0%, 100%, 0.4)),
        -webkit-linear-gradient(bottom, hsla(0, 0%, 100%, 0.9), hsla(0, 0%, 100%, 0.4));
      background-image: linear-gradient(180deg, hsla(0, 0%, 100%, 0.9), hsla(0, 0%, 100%, 0.4)),
        linear-gradient(0deg, hsla(0, 0%, 100%, 0.9), hsla(0, 0%, 100%, 0.4));
      background-repeat: no-repeat;
      background-position: top, bottom;
      transform: translateZ(0);
      pointer-events: none;
    }
  }
</style>
